import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  DndContext, PointerSensor, useSensor, useSensors, useDroppable, DragOverlay,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import {
  ArrowLeft, Plus, Eye, Save, UploadCloud, X, Palette, GitBranch, Users, Webhook, BarChart3, Bell, CalendarClock as ScheduleIcon, Share2, History, MoreHorizontal,
  Type, AlignLeft, Mail, Phone, Hash, Link2, ChevronDown, CircleDot, CheckSquare, ListChecks,
  Calendar, Clock, CalendarClock, Heading, Text as TextIcon, Star, EyeOff, Paperclip, BookmarkPlus, Database, PenLine, CreditCard, Languages, FileText,
} from 'lucide-react';
import api from '../../services/api';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import alertService from '../../utils/alert';
import { FIELD_TYPES } from '../../modules/forms/fieldTypes';
import PaletteItem from '../../modules/forms/PaletteItem';
import CrmFieldPaletteItem from '../../modules/forms/CrmFieldPaletteItem';
import { CRM_FIELD_GROUPS } from '../../modules/forms/crmFieldRegistry';
import CanvasItem from '../../modules/forms/CanvasItem';
import PropertiesPanel from '../../modules/forms/PropertiesPanel';
import FormRenderer from '../../modules/forms/FormRenderer';
import ThemePanel from '../../modules/forms/ThemePanel';
import CrmMappingPanel from '../../modules/forms/CrmMappingPanel';
import WebhooksPanel from '../../modules/forms/WebhooksPanel';
import NotificationsPanel from '../../modules/forms/NotificationsPanel';
import AvailabilityPanel from '../../modules/forms/AvailabilityPanel';
import PaymentPanel from '../../modules/forms/PaymentPanel';
import LanguagePanel from '../../modules/forms/LanguagePanel';
import SharePanel from '../../modules/forms/SharePanel';
import VersionHistoryPanel from '../../modules/forms/VersionHistoryPanel';
import AnalyticsPanel from '../../modules/forms/AnalyticsPanel';
import ConditionEditor from '../../modules/forms/ConditionEditor';
import { referenceableFields } from '../../modules/forms/conditions';

const PALETTE_ICONS = {
  text: Type, textarea: AlignLeft, email: Mail, phone: Phone, number: Hash, url: Link2,
  select: ChevronDown, radio: CircleDot, checkbox: CheckSquare, checkboxGroup: ListChecks,
  date: Calendar, time: Clock, datetime: CalendarClock, heading: Heading, paragraph: TextIcon,
  rating: Star, hidden: EyeOff, file: Paperclip, signature: PenLine,
};

const CATEGORY_LABELS = { basic: 'Basic Fields', choice: 'Choice Fields', datetime: 'Date & Time', layout: 'Layout' };

const genId = () => `field_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;

const DEFAULTS_BY_TYPE = {
  select: () => ({ options: [{ label: 'Option 1', value: 'option_1' }, { label: 'Option 2', value: 'option_2' }] }),
  radio: () => ({ options: [{ label: 'Option 1', value: 'option_1' }, { label: 'Option 2', value: 'option_2' }] }),
  checkboxGroup: () => ({ options: [{ label: 'Option 1', value: 'option_1' }, { label: 'Option 2', value: 'option_2' }] }),
  checkbox: () => ({ checkboxLabel: 'Yes' }),
  rating: () => ({ maxStars: 5 }),
  hidden: () => ({ label: 'hidden_field' }),
  heading: () => ({ label: 'Section Heading' }),
  paragraph: () => ({ text: 'Add descriptive text here.' }),
};

function newComponent(type) {
  const meta = FIELD_TYPES[type];
  return {
    id: genId(),
    type,
    label: meta?.display ? undefined : meta?.label || 'New Field',
    required: false,
    ...(DEFAULTS_BY_TYPE[type]?.() || {}),
  };
}

function DroppableCanvas({ id, children, isEmpty }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div
      ref={setNodeRef}
      className={`space-y-3 min-h-[200px] rounded-[4px] transition-colors ${isOver ? 'bg-[#0176D3]/5 ring-2 ring-[#0176D3]/20' : ''} ${isEmpty ? 'flex items-center justify-center border-2 border-dashed border-[#D8DDE6]' : ''}`}
    >
      {children}
    </div>
  );
}

export default function FormBuilderPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [pages, setPages] = useState([]);
  const [theme, setTheme] = useState({});
  const [crmMapping, setCrmMapping] = useState({ enabled: false, fieldMap: {} });
  const [webhooks, setWebhooks] = useState([]);
  const [notifications, setNotifications] = useState({ internal: { enabled: false, recipients: [] }, confirmation: { enabled: false } });
  const [payment, setPayment] = useState({ enabled: false });
  const [i18n, setI18n] = useState({ enabled: false, defaultLocale: 'en', locales: [], translations: {} });
  const [pageIndex, setPageIndex] = useState(0);
  const [selectedId, setSelectedId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showTheme, setShowTheme] = useState(false);
  const [showCrmMapping, setShowCrmMapping] = useState(false);
  const [showWebhooks, setShowWebhooks] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showAvailability, setShowAvailability] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [showLanguages, setShowLanguages] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [showPageLogic, setShowPageLogic] = useState(false);
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const moreMenuRef = useRef(null);
  const [templateName, setTemplateName] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [activeDragType, setActiveDragType] = useState(null);
  const [dirty, setDirty] = useState(false);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/forms/${id}/schema`);
        if (cancelled) return;
        setForm(data.data.form);
        setPages(data.data.version.schema?.pages?.length ? data.data.version.schema.pages : [{ id: genId(), title: 'Page 1', components: [] }]);
        setTheme(data.data.version.schema?.theme || {});
        setCrmMapping(data.data.version.schema?.settings?.crmMapping || { enabled: false, fieldMap: {} });
        setWebhooks(data.data.version.schema?.settings?.webhooks || []);
        setNotifications(data.data.version.schema?.settings?.notifications || { internal: { enabled: false, recipients: [] }, confirmation: { enabled: false } });
        setPayment(data.data.version.schema?.settings?.payment || { enabled: false });
        setI18n(data.data.version.schema?.settings?.i18n || { enabled: false, defaultLocale: 'en', locales: [], translations: {} });
      } catch (err) {
        alertService.toast('error', err.response?.data?.message || 'Failed to load form');
        navigate('/dashboard/forms');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, navigate]);

  useEffect(() => {
    if (!showMoreMenu) return;
    const onClickOutside = (e) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(e.target)) setShowMoreMenu(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [showMoreMenu]);

  const currentPage = pages[pageIndex];
  const selectedComponent = useMemo(
    () => currentPage?.components.find((c) => c.id === selectedId) || null,
    [currentPage, selectedId]
  );
  const allFields = useMemo(() => referenceableFields(pages), [pages]);
  const priorPageFields = useMemo(
    () => referenceableFields(pages.slice(0, pageIndex)),
    [pages, pageIndex]
  );

  const updatePageCondition = (condition) => {
    setPages((prev) => prev.map((p, i) => (i === pageIndex ? { ...p, condition } : p)));
    setDirty(true);
  };

  const updatePageComponents = (updater) => {
    setPages((prev) => prev.map((p, i) => (i === pageIndex ? { ...p, components: updater(p.components) } : p)));
    setDirty(true);
  };

  const addPage = () => {
    setPages((prev) => [...prev, { id: genId(), title: `Page ${prev.length + 1}`, components: [] }]);
    setPageIndex(pages.length);
    setDirty(true);
  };

  const removePage = (index) => {
    if (pages.length === 1) return;
    setPages((prev) => prev.filter((_, i) => i !== index));
    setPageIndex((prev) => Math.max(0, prev >= index ? prev - 1 : prev));
    setDirty(true);
  };

  const handleDragStart = ({ active }) => {
    if (active.data.current?.source === 'palette') setActiveDragType(active.data.current.type);
    if (active.data.current?.source === 'crm-field') setActiveDragType(`crm:${active.data.current.field.label}`);
  };

  const handleDragEnd = ({ active, over }) => {
    setActiveDragType(null);
    if (!over) return;

    if (active.data.current?.source === 'palette') {
      const type = active.data.current.type;
      const component = newComponent(type);
      updatePageComponents((components) => {
        const overIndex = components.findIndex((c) => c.id === over.id);
        if (overIndex === -1) return [...components, component];
        return [...components.slice(0, overIndex), component, ...components.slice(overIndex)];
      });
      setSelectedId(component.id);
      return;
    }

    if (active.data.current?.source === 'crm-field') {
      const field = active.data.current.field;
      const component = {
        id: genId(),
        type: field.type,
        label: field.label,
        required: !!field.required,
        crmObject: field.object,
        crmKey: field.key,
        ...(field.placeholder ? { placeholder: field.placeholder } : {}),
        ...(field.options ? { options: field.options } : {}),
        ...(field.type === 'checkbox' ? { checkboxLabel: field.label } : {}),
      };
      updatePageComponents((components) => {
        const overIndex = components.findIndex((c) => c.id === over.id);
        if (overIndex === -1) return [...components, component];
        return [...components.slice(0, overIndex), component, ...components.slice(overIndex)];
      });
      setSelectedId(component.id);
      // Lead/Contact/Account/Deal fields write back on submission
      // (Phase 15/16/17) via their own namespaced map — see
      // FormCrmMappingService's EXTRA_LEAD_FIELDS/EXTRA_CONTACT_FIELDS/
      // EXTRA_ACCOUNT_FIELDS/EXTRA_DEAL_FIELDS allowlists. Deal's `stage`
      // key is deliberately excluded from that allowlist even if dragged in
      // — the admin picks a fixed Pipeline + Stage in the CRM Mapping panel
      // instead, since a public submission can't be trusted to name a valid
      // stage on the company's pipeline.
      const mapKeyByObject = {
        lead: 'fieldMap', contact: 'contactFieldMap', account: 'accountFieldMap', deal: 'dealFieldMap', task: 'taskFieldMap',
      };
      const mapKey = mapKeyByObject[field.object];
      if (mapKey) {
        setCrmMapping((prev) => ({
          ...prev,
          enabled: true,
          [mapKey]: { ...prev[mapKey], [field.key]: component.id },
        }));
        setDirty(true);
      }
      return;
    }

    if (active.id !== over.id) {
      updatePageComponents((components) => {
        const oldIndex = components.findIndex((c) => c.id === active.id);
        const newIndex = components.findIndex((c) => c.id === over.id);
        if (oldIndex === -1 || newIndex === -1) return components;
        return arrayMove(components, oldIndex, newIndex);
      });
    }
  };

  const deleteComponent = (componentId) => {
    updatePageComponents((components) => components.filter((c) => c.id !== componentId));
    if (selectedId === componentId) setSelectedId(null);
  };

  const duplicateComponent = (componentId) => {
    updatePageComponents((components) => {
      const idx = components.findIndex((c) => c.id === componentId);
      if (idx === -1) return components;
      const clone = { ...components[idx], id: genId() };
      return [...components.slice(0, idx + 1), clone, ...components.slice(idx + 1)];
    });
  };

  const updateSelectedComponent = (updated) => {
    updatePageComponents((components) => components.map((c) => (c.id === updated.id ? updated : c)));
  };

  const updateTheme = (next) => {
    setTheme(next);
    setDirty(true);
  };

  const updateCrmMapping = (next) => {
    setCrmMapping(next);
    setDirty(true);
  };

  const updateWebhooks = (next) => {
    setWebhooks(next);
    setDirty(true);
  };

  const updateNotifications = (next) => {
    setNotifications(next);
    setDirty(true);
  };

  const updatePayment = (next) => {
    setPayment(next);
    setDirty(true);
  };

  const updateI18n = (next) => {
    setI18n(next);
    setDirty(true);
  };

  const testWebhook = async (webhook) => {
    const { data } = await api.post(`/forms/${id}/webhooks/test`, { url: webhook.url, secret: webhook.secret });
    return data.data;
  };

  const fetchAnalytics = async () => {
    const { data } = await api.get(`/forms/${id}/analytics`);
    return data.data;
  };

  const saveAvailability = async (patch) => {
    const { data } = await api.put(`/forms/${id}/availability`, patch);
    setForm(data.data.form);
    alertService.toast('success', 'Scheduling & limits updated');
  };

  const openSaveTemplate = () => {
    setTemplateName(`${form?.name || 'Form'} Template`);
    setShowSaveTemplate(true);
  };

  const saveAsTemplate = async () => {
    if (!templateName.trim()) return;
    setSavingTemplate(true);
    try {
      await api.post(`/forms/${id}/save-as-template`, { name: templateName.trim() });
      alertService.toast('success', 'Saved as template');
      setShowSaveTemplate(false);
    } catch (err) {
      alertService.toast('error', err.response?.data?.message || 'Failed to save as template');
    } finally {
      setSavingTemplate(false);
    }
  };

  const buildSchema = () => ({ settings: { crmMapping, webhooks, notifications, payment, i18n }, theme, pages });

  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await api.post(`/forms/${id}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data.data;
  };

  const saveDraft = async () => {
    setSaving(true);
    try {
      await api.put(`/forms/${id}`, { schema: buildSchema() });
      setDirty(false);
      alertService.toast('success', 'Draft saved');
    } catch (err) {
      alertService.toast('error', err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    setPublishing(true);
    try {
      await api.put(`/forms/${id}`, { schema: buildSchema() });
      await api.post(`/forms/${id}/publish`);
      setDirty(false);
      alertService.toast('success', 'Form published');
    } catch (err) {
      alertService.toast('error', err.response?.data?.message || 'Failed to publish');
    } finally {
      setPublishing(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-screen text-[#706E6B] text-[13px]">Loading builder…</div>;
  }

  return (
    <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <div className="flex flex-col h-screen bg-[#F4F6F9]">
        {/* Top bar */}
        <div className="h-14 shrink-0 bg-white border-b border-[#D8DDE6] flex items-center justify-between px-4">
          <div className="flex items-center gap-3 min-w-0">
            <button onClick={() => navigate('/dashboard/forms')} className="p-2 text-[#706E6B] hover:text-[#181818] rounded-[4px] hover:bg-[#F4F6F9]">
              <ArrowLeft size={17} />
            </button>
            <div className="min-w-0">
              <p className="text-[13px] font-bold text-[#080707] truncate">{form?.name}</p>
            </div>
            <Badge variant={form?.status === 'published' ? 'success' : 'slate'}>{form?.status}</Badge>
            {dirty && <span className="text-[10px] font-bold text-[#706E6B]">Unsaved changes</span>}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="outline" icon={Palette} onClick={() => setShowTheme(true)}>Design</Button>

            {/* Everything below used to be 12 separate buttons on this same
                row — on any screen narrower than ~1700px they wrapped,
                overlapped the title, or got clipped entirely. Consolidated
                into one menu; only the handful of actions used on nearly
                every edit (Design, Preview, Save, Publish) stay as their
                own buttons. */}
            <div className="relative" ref={moreMenuRef}>
              <Button variant="outline" icon={MoreHorizontal} onClick={() => setShowMoreMenu((v) => !v)}>
                More
              </Button>
              {showMoreMenu && (
                <div className="absolute top-full right-0 mt-2 z-30 bg-white border border-[#D8DDE6] rounded-[8px] shadow-[0_2px_8px_rgba(0,0,0,0.16)] py-1.5 w-56">
                  {[
                    { icon: Users, label: 'CRM Mapping', onClick: () => setShowCrmMapping(true) },
                    { icon: Webhook, label: 'Webhooks', onClick: () => setShowWebhooks(true) },
                    { icon: Bell, label: 'Notifications', onClick: () => setShowNotifications(true) },
                    { icon: ScheduleIcon, label: 'Scheduling', onClick: () => setShowAvailability(true) },
                    { icon: CreditCard, label: 'Payment', onClick: () => setShowPayment(true) },
                    { icon: Languages, label: 'Languages', onClick: () => setShowLanguages(true) },
                    { icon: BarChart3, label: 'Analytics', onClick: () => setShowAnalytics(true) },
                    { icon: FileText, label: 'Submissions', onClick: () => navigate(`/dashboard/forms/${id}/submissions`) },
                    { icon: Share2, label: 'Share', onClick: () => setShowShare(true) },
                    { icon: History, label: 'Version History', onClick: () => setShowVersionHistory(true) },
                    { icon: BookmarkPlus, label: 'Save as Template', onClick: openSaveTemplate },
                  ].map(({ icon: Icon, label, onClick }) => (
                    <button
                      key={label}
                      onClick={() => { setShowMoreMenu(false); onClick(); }}
                      className="w-full flex items-center gap-2.5 text-left px-4 py-2 text-[13px] font-medium text-[#080707] hover:bg-[#F4F6F9] transition-colors"
                    >
                      <Icon size={15} className="text-[#706E6B]" />
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="w-px h-6 bg-[#D8DDE6]" />

            <Button variant="outline" icon={Eye} onClick={() => setShowPreview(true)}>Preview</Button>
            <Button variant="outline" icon={Save} loading={saving} onClick={saveDraft}>Save Draft</Button>
            <Button variant="brand" icon={UploadCloud} loading={publishing} onClick={publish}>Publish</Button>
          </div>
        </div>

        <div className="flex-1 flex overflow-hidden">
          {/* Palette */}
          <div className="w-[240px] shrink-0 bg-white border-r border-[#D8DDE6] overflow-y-auto p-4 space-y-5">
            {Object.entries(CATEGORY_LABELS).map(([category, label]) => (
              <div key={category}>
                <p className="text-[10px] font-bold text-[#706E6B] mb-2">{label}</p>
                <div className="space-y-1.5">
                  {Object.entries(FIELD_TYPES)
                    .filter(([, meta]) => meta.category === category)
                    .map(([type, meta]) => (
                      <PaletteItem key={type} type={type} label={meta.label} icon={PALETTE_ICONS[type]} />
                    ))}
                </div>
              </div>
            ))}

            <div className="pt-1 border-t border-[#F0F1F3]">
              <p className="text-[10px] font-bold text-[#706E6B] mb-2 flex items-center gap-1.5">
                <Database size={11} /> CRM Fields
              </p>
              <p className="text-[10px] text-[#706E6B] mb-2 leading-snug">
                Drop these in to build a form mapped straight to your CRM. Lead fields write back automatically.
              </p>
              <div className="space-y-3">
                {CRM_FIELD_GROUPS.map((group) => (
                  <details key={group.object} className="group">
                    <summary className="text-[11px] font-bold text-[#181818] cursor-pointer list-none flex items-center gap-1 select-none">
                      <ChevronDown size={12} className="text-[#706E6B] transition-transform group-open:rotate-0 -rotate-90" />
                      {group.label}
                    </summary>
                    <div className="space-y-1.5 mt-1.5 pl-1">
                      {group.fields.map((field) => (
                        <CrmFieldPaletteItem key={field.key} field={{ ...field, object: group.object }} />
                      ))}
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </div>

          {/* Canvas */}
          <div className="flex-1 overflow-y-auto p-6">
            <div className="max-w-[720px] mx-auto space-y-4">
              {/* Page tabs */}
              <div className="flex items-center gap-2 flex-wrap">
                {pages.map((page, i) => (
                  <button
                    key={page.id}
                    onClick={() => { setPageIndex(i); setShowPageLogic(false); }}
                    className={`group flex items-center gap-1.5 px-3 py-1.5 rounded-[4px] text-[12px] font-bold transition-colors ${
                      i === pageIndex ? 'bg-[#0176D3] text-white' : 'bg-white border border-[#D8DDE6] text-[#706E6B] hover:border-[#0176D3]'
                    }`}
                  >
                    {page.title || `Page ${i + 1}`}
                    {page.condition?.enabled && <GitBranch size={11} className={i === pageIndex ? 'text-white' : 'text-[#0176D3]'} />}
                    {pages.length > 1 && (
                      <X
                        size={12}
                        className={`opacity-0 group-hover:opacity-100 ${i === pageIndex ? 'text-white' : 'text-[#706E6B]'}`}
                        onClick={(e) => { e.stopPropagation(); removePage(i); }}
                      />
                    )}
                  </button>
                ))}
                <button onClick={addPage} className="flex items-center gap-1 px-3 py-1.5 rounded-[4px] text-[12px] font-bold text-[#0176D3] border border-dashed border-[#0176D3]/40 hover:bg-[#0176D3]/5 whitespace-nowrap">
                  <Plus size={13} /> Add Page
                </button>
              </div>

              <div className="flex items-center gap-2">
                <input
                  className="flex-1 text-[16px] font-bold text-[#181818] bg-transparent border-none outline-none px-1"
                  value={currentPage?.title || ''}
                  onChange={(e) => setPages((prev) => prev.map((p, i) => (i === pageIndex ? { ...p, title: e.target.value } : p)))}
                  placeholder="Page title"
                />
                {pageIndex > 0 && (
                  <button
                    onClick={() => setShowPageLogic((v) => !v)}
                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-[4px] text-[11px] font-bold border transition-colors shrink-0  whitespace-nowrap ${
                      currentPage?.condition?.enabled
                        ? 'border-[#0176D3] bg-[#0176D3]/5 text-[#0176D3]'
                        : 'border-[#D8DDE6] text-[#706E6B] hover:border-[#0176D3]/40'
                    }`}
                  >
                    <GitBranch size={12} /> Page Logic
                  </button>
                )}
              </div>

              {showPageLogic && pageIndex > 0 && (
                <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4">
                  <ConditionEditor
                    condition={currentPage?.condition}
                    fields={priorPageFields}
                    onChange={updatePageCondition}
                    title="Skip this page unless…"
                    emptyLabel="this page"
                  />
                </div>
              )}

              <div
                style={{
                  '--form-primary': theme.primaryColor || '#0176D3',
                  '--form-radius': theme.borderRadius != null ? `${theme.borderRadius}px` : '4px',
                  fontFamily: theme.fontFamily || undefined,
                }}
              >
                <SortableContext items={currentPage?.components.map((c) => c.id) || []} strategy={verticalListSortingStrategy}>
                  <DroppableCanvas id="canvas-drop" isEmpty={!currentPage?.components.length}>
                    {currentPage?.components.length ? (
                      <div className="flex flex-wrap gap-3 w-full">
                        {currentPage.components.map((component) => (
                          <div key={component.id} className={component.width === 'half' ? 'w-full sm:w-[calc(50%-0.375rem)]' : 'w-full'}>
                            <CanvasItem
                              component={component}
                              selected={component.id === selectedId}
                              onSelect={setSelectedId}
                              onDelete={deleteComponent}
                              onDuplicate={duplicateComponent}
                            />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-[13px] text-[#706E6B] py-8">Drag a field from the left to start building this page.</p>
                    )}
                  </DroppableCanvas>
                </SortableContext>
              </div>
            </div>
          </div>

          {/* Properties panel */}
          <div className="w-[300px] shrink-0 bg-white border-l border-[#D8DDE6] overflow-y-auto">
            <PropertiesPanel component={selectedComponent} allFields={allFields} onChange={updateSelectedComponent} onClose={() => setSelectedId(null)} />
          </div>
        </div>
      </div>

      <DragOverlay>
        {activeDragType ? (
          <div className="px-3 py-2.5 rounded-[4px] border border-[#0176D3] bg-white shadow-[0_2px_8px_rgba(0,0,0,0.16)] text-[12px] font-medium text-[#181818]">
            {activeDragType.startsWith('crm:') ? activeDragType.slice(4) : FIELD_TYPES[activeDragType]?.label}
          </div>
        ) : null}
      </DragOverlay>

      {showTheme && (
        <ThemePanel theme={theme} onChange={updateTheme} onClose={() => setShowTheme(false)} />
      )}

      {showCrmMapping && (
        <CrmMappingPanel
          mapping={crmMapping}
          fields={allFields}
          onChange={updateCrmMapping}
          onClose={() => setShowCrmMapping(false)}
        />
      )}

      {showWebhooks && (
        <WebhooksPanel
          webhooks={webhooks}
          onChange={updateWebhooks}
          onTest={testWebhook}
          onClose={() => setShowWebhooks(false)}
        />
      )}

      {showAnalytics && (
        <AnalyticsPanel formId={id} onClose={() => setShowAnalytics(false)} fetchAnalytics={fetchAnalytics} />
      )}

      {showNotifications && (
        <NotificationsPanel
          notifications={notifications}
          fields={allFields}
          onChange={updateNotifications}
          onClose={() => setShowNotifications(false)}
        />
      )}

      {showShare && form && (
        <SharePanel form={form} onClose={() => setShowShare(false)} />
      )}

      {showVersionHistory && form && (
        <VersionHistoryPanel
          formId={id}
          currentVersion={form.currentVersion}
          onClose={() => setShowVersionHistory(false)}
          onRestored={() => window.location.reload()}
        />
      )}

      {showAvailability && form && (
        <AvailabilityPanel form={form} onSave={saveAvailability} onClose={() => setShowAvailability(false)} />
      )}

      {showPayment && (
        <PaymentPanel
          payment={payment}
          fields={allFields}
          onChange={updatePayment}
          onClose={() => setShowPayment(false)}
        />
      )}

      {showLanguages && (
        <LanguagePanel
          pages={pages}
          i18n={i18n}
          onChange={updateI18n}
          onClose={() => setShowLanguages(false)}
        />
      )}

      {showSaveTemplate && (
        <div className="fixed inset-0 z-[100] bg-black/40 flex items-end sm:items-center justify-center sm:p-4" onClick={() => setShowSaveTemplate(false)}>
          <div className="w-full max-w-[420px] max-h-[100dvh] overflow-y-auto bg-white rounded-t-lg sm:rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.16)] p-4 sm:p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between -mx-4 sm:-mx-6 -mt-4 sm:-mt-6 mb-4 px-4 sm:px-6 py-3 border-b border-[#E5E5E5]">
              <p className="text-[16px] font-bold text-[#2E2E2E]">Save as Template</p>
              <button onClick={() => setShowSaveTemplate(false)} className="text-[#706E6B] hover:text-[#181818]"><X size={18} /></button>
            </div>
            <label className="text-[11px] font-semibold text-[#706E6B] block mb-1.5">Template Name</label>
            <input
              className="w-full px-3 py-2 border border-[#D8DDE6] rounded text-[13px] text-[#181818] focus:outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3] transition-all"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && saveAsTemplate()}
            />
            <p className="text-[11px] text-[#706E6B] mt-2">
              The current saved schema of this form will be cloned into a new, unpublished template you can reuse later.
            </p>
            <button
              onClick={saveAsTemplate}
              disabled={!templateName.trim() || savingTemplate}
              className="mt-4 w-full px-4 h-9 rounded bg-[#0176D3] hover:bg-[#014486] text-white text-[13px] font-semibold disabled:opacity-60 transition-colors"
            >
              {savingTemplate ? 'Saving…' : 'Save as Template'}
            </button>
          </div>
        </div>
      )}

      {showPreview && (
        <div className="fixed inset-0 z-[100] bg-black/40 flex items-end sm:items-center justify-center sm:p-4" onClick={() => setShowPreview(false)}>
          <div className="w-full max-w-[640px] max-h-[100dvh] overflow-y-auto bg-white rounded-t-lg sm:rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.16)] p-4 sm:p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between -mx-4 sm:-mx-6 -mt-4 sm:-mt-6 mb-4 px-4 sm:px-6 py-3 border-b border-[#E5E5E5]">
              <p className="text-[16px] font-bold text-[#2E2E2E]">Live Preview</p>
              <button onClick={() => setShowPreview(false)} className="text-[#706E6B] hover:text-[#181818]"><X size={18} /></button>
            </div>
            <FormRenderer
              schema={buildSchema()}
              onSubmit={async () => alertService.toast('info', 'Preview only — nothing was submitted')}
              onUploadFile={uploadFile}
            />
          </div>
        </div>
      )}
    </DndContext>
  );
}
